import { EEntityType } from '.';

export enum EVisibleEntityType {
  ColumnAndWall = 'columnAndWall',
  RegionTextInfo = 'regionTextInfo',
  RoomTextInfo = 'roomTextInfo',
  Ruler = 'ruler'
}

/**
 * 显隐开关状态。注意面板上两类开关的语义并不相同：
 *
 * A. 继承式（roomTextInfo / regionTextInfo / ruler / floorPlan）
 *    这里的布尔就是真源。对应 manager 每次刷新都重读它，新建实体自动继承隐藏状态，恒为二态。
 *
 * B. 快照式（columnAndWall / furniture / doorWindow / separateWall / areaDimension）
 *    真源是 `VisibilityStore._hiddenEntitiesList`：隐藏时只快照当时已存在的 id，之后新建的构件正常显示。
 *    这里的布尔**只表示用户最后一次拨动开关的意图**，仅在该分组当前一个实体都没有时，
 *    用来决定勾选框显示成打勾还是空白（见 `getVisibleStatus`）。
 *    任何判断「现在到底显不显示」的逻辑都不要读它，请读 `getVisibleStatus()`。
 */
export type IVisibleState = Partial<
  Pick<
    Record<EVisibleEntityType | EEntityType, boolean>,
    | EVisibleEntityType
    | EEntityType.FloorPlan
    | EEntityType.Furniture
    | EEntityType.SeparateWall
    | EEntityType.DoorWindow
    | EEntityType.AreaDimension
  >
>;

/** 显隐面板上一行开关对应的 key */
export type TVisibleKey = keyof Required<IVisibleState>;

/** 开关 key 与实体类型一一对应的那部分，可以直接走通用的单类型显隐路径 */
export type TVisibleEntityKey = Extract<TVisibleKey, EEntityType>;

/** 显隐三态。Partial 只可能出现在含快照式隐藏集的分组上 */
export enum EVisibleStatus {
  Visible = 'visible',
  Hidden = 'hidden',
  /** 部分隐藏：该分组下既有被隐藏的实体，也有可见的实体 */
  Partial = 'partial'
}

export type TVisibleStatusMap = Record<TVisibleKey, EVisibleStatus>;

/**
 * 一行开关 = 若干「单位」的聚合，三态由 `隐藏单位数 / 总单位数` 推导
 *
 * - entityTypes：快照式隐藏集。每个实体算 1 个单位，是否隐藏看它在不在隐藏集里
 * - inherited：继承式全局开关。整体算 1 个单位，是否隐藏看 `visibleState[key] === false`
 *
 * 只有 1 个继承单位、没有 entityTypes 的分组，隐藏单位数只能是 0 或 1，
 * 数学上不可能落到 Partial —— 二态是天然成立的，不需要额外特判。
 */
export const VISIBLE_GROUPS: Record<TVisibleKey, { entityTypes: EEntityType[]; inherited: boolean }> = {
  [EVisibleEntityType.RoomTextInfo]: { entityTypes: [], inherited: true },
  [EVisibleEntityType.RegionTextInfo]: { entityTypes: [], inherited: true },
  [EEntityType.FloorPlan]: { entityTypes: [], inherited: true },
  // 尺寸线一个开关管两样东西：墙体标尺是继承式（rulerManager 每次全量重读开关），
  // 手绘尺寸标注是快照式。所以新画墙不增加单位，只有 drawLinearDimension 才会
  [EVisibleEntityType.Ruler]: { entityTypes: [EEntityType.LinearDimension], inherited: true },
  [EVisibleEntityType.ColumnAndWall]: { entityTypes: [EEntityType.Wall, EEntityType.Column], inherited: false },
  [EEntityType.Furniture]: { entityTypes: [EEntityType.Furniture, EEntityType.Assembly], inherited: false },
  [EEntityType.DoorWindow]: { entityTypes: [EEntityType.DoorWindow], inherited: false },
  [EEntityType.SeparateWall]: { entityTypes: [EEntityType.SeparateWall], inherited: false },
  [EEntityType.AreaDimension]: { entityTypes: [EEntityType.AreaDimension], inherited: false }
};

export const VISIBLE_KEYS = Object.keys(VISIBLE_GROUPS) as TVisibleKey[];
